import { useEffect, useState } from 'react'
import { useAppStore } from '@/stores/appStore'
import { api, Org, Team, OrgMember, Tag } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Building2, Users, Plus, Trash2, Settings, UserCog, Tag as TagIcon, Edit, Cog, FileText } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function SettingsPage() {
  const { user, orgs, refreshOrgs } = useAppStore()

  // User management state
  const [systemUsers, setSystemUsers] = useState<{ id: string; email: string; display_name: string; is_admin?: boolean; created_at?: string }[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [allowRegularUsersPublicFileLinks, setAllowRegularUsersPublicFileLinks] = useState(true)
  const [isLoadingPublicShareSetting, setIsLoadingPublicShareSetting] = useState(false)
  const [isSavingPublicShareSetting, setIsSavingPublicShareSetting] = useState(false)

  // Org management state
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgSlug, setNewOrgSlug] = useState('')
  const [isCreatingOrg, setIsCreatingOrg] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null)
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([])
  const [teamOrgMembers, setTeamOrgMembers] = useState<OrgMember[]>([])
  const [isLoadingTeamOrgMembers, setIsLoadingTeamOrgMembers] = useState(false)
  const [isAddUserToOrgOpen, setIsAddUserToOrgOpen] = useState(false)
  const [newOrgMemberUserId, setNewOrgMemberUserId] = useState('')
  const [newOrgMemberRole, setNewOrgMemberRole] = useState('member')
  const [isAddingOrgMember, setIsAddingOrgMember] = useState(false)

  // Team management state
  const [isCreateTeamOpen, setIsCreateTeamOpen] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamSlug, setNewTeamSlug] = useState('')
  const [newTeamOrgId, setNewTeamOrgId] = useState('')
  const [isCreatingTeam, setIsCreatingTeam] = useState(false)
  const [allTeams, setAllTeams] = useState<{ team: Team; org: Org }[]>([])
  const [selectedTeam, setSelectedTeam] = useState<{ team: Team; org: Org } | null>(null)
  const [teamDetails, setTeamDetails] = useState<Team | null>(null)
  const [isLoadingTeam, setIsLoadingTeam] = useState(false)
  const [newTeamMemberUserId, setNewTeamMemberUserId] = useState('')
  const [newTeamMemberRole, setNewTeamMemberRole] = useState('member')
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false)

  // Tags management state
  const [tagsOrgId, setTagsOrgId] = useState('')
  const [orgTags, setOrgTags] = useState<Tag[]>([])
  const [isLoadingTags, setIsLoadingTags] = useState(false)
  const [isCreateTagOpen, setIsCreateTagOpen] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#3b82f6')
  const [isCreatingTag, setIsCreatingTag] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [editTagName, setEditTagName] = useState('')
  const [editTagColor, setEditTagColor] = useState('')

  // Logs management state
  const [logs, setLogs] = useState<{ timestamp: string; level: string; source: string; message: string }[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)
  const [logsFilter, setLogsFilter] = useState<'all' | 'error' | 'warn' | 'info'>('all')

  // Load orgs on mount
  useEffect(() => {
    refreshOrgs()
  }, [refreshOrgs])

  // Load all teams across all orgs
  useEffect(() => {
    const loadAllTeams = async () => {
      const teamsList: { team: Team; org: Org }[] = []
      for (const org of orgs) {
        try {
          const { teams: orgTeams } = await api.teams.list(org.id)
          for (const team of orgTeams || []) {
            teamsList.push({ team, org })
          }
        } catch {
          toast({
            title: `Failed to load teams for ${org.name}`,
            variant: 'destructive',
          })
        }
      }
      setAllTeams(teamsList)
    }
    if (orgs.length > 0) {
      loadAllTeams()
    }
  }, [orgs])

  // Load users (admin only)
  useEffect(() => {
    const loadUsers = async () => {
      if (!user?.is_admin) {
        setSystemUsers([])
        return
      }
      try {
        setIsLoadingUsers(true)
        const { users } = await api.admin.users.list()
        setSystemUsers(users || [])
      } catch {
        setSystemUsers([])
      } finally {
        setIsLoadingUsers(false)
      }
    }
    loadUsers()
  }, [user?.is_admin])

  useEffect(() => {
    const loadPublicSharingSetting = async () => {
      if (!user?.is_admin) return
      try {
        setIsLoadingPublicShareSetting(true)
        const res = await api.admin.settings.getPublicSharing()
        setAllowRegularUsersPublicFileLinks(!!res.allow_regular_users_public_file_links)
      } catch {
        setAllowRegularUsersPublicFileLinks(true)
      } finally {
        setIsLoadingPublicShareSetting(false)
      }
    }
    loadPublicSharingSetting()
  }, [user?.is_admin])

  const handleSavePublicSharingSetting = async () => {
    try {
      setIsSavingPublicShareSetting(true)
      const res = await api.admin.settings.updatePublicSharing(allowRegularUsersPublicFileLinks)
      setAllowRegularUsersPublicFileLinks(!!res.allow_regular_users_public_file_links)
      toast({ title: 'Public sharing policy updated' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to update policy', variant: 'destructive' })
    } finally {
      setIsSavingPublicShareSetting(false)
    }
  }

  const handleToggleAdmin = async (targetUserId: string, nextIsAdmin: boolean) => {
    try {
      const { user: updated } = await api.admin.users.setAdmin(targetUserId, nextIsAdmin)
      setSystemUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, is_admin: updated.is_admin } : u)))
      toast({ title: 'User updated' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to update user', variant: 'destructive' })
    }
  }

  // Load system logs
  const loadLogs = async () => {
    if (!user?.is_admin) return
    try {
      setIsLoadingLogs(true)
      const { logs: logEntries } = await api.admin.logs.get(500)
      setLogs(logEntries || [])
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to load logs', variant: 'destructive' })
    } finally {
      setIsLoadingLogs(false)
    }
  }

  // Load org members when an org is selected (Orgs tab)
  useEffect(() => {
    const loadOrgMembers = async () => {
      const orgId = selectedOrg?.id
      if (!orgId) {
        setOrgMembers([])
        return
      }
      try {
        const { members } = await api.orgs.members.list(orgId)
        setOrgMembers(members || [])
      } catch (e) {
        setOrgMembers([])
        toast({ title: (e as Error)?.message || 'Failed to load organization members', variant: 'destructive' })
      }
    }
    loadOrgMembers()
  }, [selectedOrg?.id])

  // Load org members for the selected team's org (Teams tab)
  useEffect(() => {
    const loadTeamOrgMembers = async () => {
      const orgId = selectedTeam?.org.id
      if (!orgId) {
        setTeamOrgMembers([])
        return
      }
      try {
        setIsLoadingTeamOrgMembers(true)
        const { members } = await api.orgs.members.list(orgId)
        setTeamOrgMembers(members || [])
      } catch (e) {
        setTeamOrgMembers([])
        toast({ title: (e as Error)?.message || 'Failed to load team organization members', variant: 'destructive' })
      } finally {
        setIsLoadingTeamOrgMembers(false)
      }
    }
    loadTeamOrgMembers()
  }, [selectedTeam?.org.id])

  // Load team details (members) when a team is selected
  useEffect(() => {
    const loadTeam = async () => {
      if (!selectedTeam) {
        setTeamDetails(null)
        return
      }
      try {
        setIsLoadingTeam(true)
        const { team } = await api.teams.get(selectedTeam.org.id, selectedTeam.team.id)
        setTeamDetails(team)
      } catch (e) {
        setTeamDetails(null)
        toast({ title: (e as Error)?.message || 'Failed to load team', variant: 'destructive' })
      } finally {
        setIsLoadingTeam(false)
      }
    }
    loadTeam()
  }, [selectedTeam])

  const handleAddOrgMember = async () => {
    if (!selectedOrg || !newOrgMemberUserId) return
    try {
      setIsAddingOrgMember(true)
      await api.orgs.members.add(selectedOrg.id, {
        user_id: newOrgMemberUserId,
        role: newOrgMemberRole,
      })
      const { members } = await api.orgs.members.list(selectedOrg.id)
      setOrgMembers(members || [])
      setNewOrgMemberUserId('')
      setNewOrgMemberRole('member')
      setIsAddUserToOrgOpen(false)
      toast({ title: 'Member added' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to add member', variant: 'destructive' })
    } finally {
      setIsAddingOrgMember(false)
    }
  }

  const handleUpdateOrgMemberRole = async (memberUserId: string, nextRole: string) => {
    if (!selectedOrg) return
    try {
      await api.orgs.members.update(selectedOrg.id, memberUserId, { role: nextRole })
      setOrgMembers((prev) => prev.map((m) => (m.user_id === memberUserId ? { ...m, role: nextRole } : m)))
      toast({ title: 'Role updated' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to update role', variant: 'destructive' })
    }
  }

  const handleRemoveOrgMember = async (memberUserId: string) => {
    if (!selectedOrg) return
    if (!confirm('Remove this member from the organization?')) return
    try {
      await api.orgs.members.remove(selectedOrg.id, memberUserId)
      setOrgMembers((prev) => prev.filter((m) => m.user_id !== memberUserId))
      toast({ title: 'Member removed' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to remove member', variant: 'destructive' })
    }
  }

  const handleAddTeamMember = async () => {
    if (!selectedTeam || !newTeamMemberUserId) return
    try {
      setIsAddingTeamMember(true)
      const { member } = await api.teams.addMember(selectedTeam.org.id, selectedTeam.team.id, {
        user_id: newTeamMemberUserId,
        role: newTeamMemberRole,
      })
      setTeamDetails((prev) => {
        if (!prev) return prev
        const nextMembers = [...(prev.members || [])]
        const user = orgMembers.find((m) => m.user_id === member.user_id)
        nextMembers.push({
          id: member.id,
          user_id: member.user_id,
          role: member.role,
          email: user?.email,
          display_name: user?.display_name,
          created_at: member.created_at,
        })
        return { ...prev, members: nextMembers }
      })
      setNewTeamMemberUserId('')
      setNewTeamMemberRole('member')
      toast({ title: 'Team member added' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to add team member', variant: 'destructive' })
    } finally {
      setIsAddingTeamMember(false)
    }
  }

  const handleRemoveTeamMember = async (memberUserId: string) => {
    if (!selectedTeam) return
    if (!confirm('Remove this member from the team?')) return
    try {
      await api.teams.removeMember(selectedTeam.org.id, selectedTeam.team.id, memberUserId)
      setTeamDetails((prev) => {
        if (!prev) return prev
        return { ...prev, members: (prev.members || []).filter((m) => m.user_id !== memberUserId) }
      })
      toast({ title: 'Team member removed' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to remove team member', variant: 'destructive' })
    }
  }

  const handleCreateOrg = async () => {
    if (!newOrgName.trim() || !newOrgSlug.trim()) return

    try {
      setIsCreatingOrg(true)
      await api.orgs.create({
        name: newOrgName.trim(),
        slug: newOrgSlug.trim(),
      })
      toast({ title: 'Organization created' })
      await refreshOrgs()
      setIsCreateOrgOpen(false)
      setNewOrgName('')
      setNewOrgSlug('')
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to create organization', variant: 'destructive' })
    } finally {
      setIsCreatingOrg(false)
    }
  }

  const handleCreateTeam = async () => {
    if (!newTeamName.trim() || !newTeamSlug.trim() || !newTeamOrgId) return

    try {
      setIsCreatingTeam(true)
      await api.teams.create(newTeamOrgId, {
        name: newTeamName.trim(),
        slug: newTeamSlug.trim(),
      })
      toast({ title: 'Team created' })
      // Reload teams
      const teamsList: { team: Team; org: Org }[] = []
      for (const org of orgs) {
        try {
          const { teams: orgTeams } = await api.teams.list(org.id)
          for (const team of orgTeams || []) {
            teamsList.push({ team, org })
          }
        } catch {
          // Ignore
        }
      }
      setAllTeams(teamsList)
      setIsCreateTeamOpen(false)
      setNewTeamName('')
      setNewTeamSlug('')
      setNewTeamOrgId('')
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to create team', variant: 'destructive' })
    } finally {
      setIsCreatingTeam(false)
    }
  }

  const handleDeleteTeam = async (team: Team, org: Org) => {
    if (!confirm(`Are you sure you want to delete "${team.name}"?`)) return

    try {
      await api.teams.delete(org.id, team.id)
      toast({ title: 'Team deleted' })
      setAllTeams(allTeams.filter(t => t.team.id !== team.id))
    } catch {
      toast({ title: 'Failed to delete team', variant: 'destructive' })
    }
  }

  // Load tags when org is selected for tags management
  useEffect(() => {
    const loadTags = async () => {
      if (!tagsOrgId) {
        setOrgTags([])
        return
      }
      try {
        setIsLoadingTags(true)
        const { tags } = await api.tags.list(tagsOrgId)
        setOrgTags(tags || [])
      } catch {
        setOrgTags([])
      } finally {
        setIsLoadingTags(false)
      }
    }
    loadTags()
  }, [tagsOrgId])

  // Auto-select first org for tags if none selected
  useEffect(() => {
    if (!tagsOrgId && orgs.length > 0) {
      setTagsOrgId(orgs[0].id)
    }
  }, [orgs, tagsOrgId])

  const handleCreateTag = async () => {
    if (!tagsOrgId || !newTagName.trim()) return
    try {
      setIsCreatingTag(true)
      const { tag } = await api.tags.create(tagsOrgId, {
        name: newTagName.trim(),
        color: newTagColor || undefined,
      })
      setOrgTags((prev) => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)))
      setNewTagName('')
      setNewTagColor('#3b82f6')
      setIsCreateTagOpen(false)
      toast({ title: 'Tag created' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to create tag', variant: 'destructive' })
    } finally {
      setIsCreatingTag(false)
    }
  }

  const handleUpdateTag = async () => {
    if (!editingTag || !tagsOrgId) return
    try {
      const { tag } = await api.tags.update(tagsOrgId, editingTag.id, {
        name: editTagName.trim() || undefined,
        color: editTagColor || undefined,
      })
      setOrgTags((prev) => prev.map((t) => (t.id === tag.id ? tag : t)))
      setEditingTag(null)
      setEditTagName('')
      setEditTagColor('')
      toast({ title: 'Tag updated' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to update tag', variant: 'destructive' })
    }
  }

  const handleDeleteTag = async (tagId: string) => {
    if (!tagsOrgId) return
    if (!confirm('Delete this tag? It will be removed from all files.')) return
    try {
      await api.tags.delete(tagsOrgId, tagId)
      setOrgTags((prev) => prev.filter((t) => t.id !== tagId))
      toast({ title: 'Tag deleted' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to delete tag', variant: 'destructive' })
    }
  }

  const startEditTag = (tag: Tag) => {
    setEditingTag(tag)
    setEditTagName(tag.name)
    setEditTagColor(tag.color || '#3b82f6')
  }

  // Available users for adding to org (exclude already members)
  const availableUsersForOrg = systemUsers.filter(
    (u) => !orgMembers.some((m) => m.user_id === u.id)
  )

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-2xl font-bold mb-6">Admin Panel</h1>

      {!user?.is_admin ? (
        <div className="border rounded-lg p-6 text-muted-foreground">
          You do not have access to admin settings.
        </div>
      ) : (
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <UserCog className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="orgs" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Orgs
          </TabsTrigger>
          <TabsTrigger value="teams" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="tags" className="flex items-center gap-2">
            <TagIcon className="h-4 w-4" />
            Tags
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Cog className="h-4 w-4" />
            System Settings
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Logs
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
        <section className="border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <UserCog className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">System Users</h2>
            </div>
          </div>

          {isLoadingUsers ? (
            <p className="text-sm text-muted-foreground">Loading users…</p>
          ) : systemUsers.length > 0 ? (
            <div className="space-y-2">
              {systemUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">{u.display_name || u.email}</p>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                  </div>
                  <Button
                    variant={u.is_admin ? 'default' : 'outline'}
                    size="sm"
                    disabled={u.id === user?.id}
                    onClick={() => handleToggleAdmin(u.id, !u.is_admin)}
                  >
                    {u.id === user?.id ? 'You' : u.is_admin ? 'Admin' : 'Make Admin'}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No users found.</p>
          )}
        </section>
        </TabsContent>

        {/* Orgs Tab */}
        <TabsContent value="orgs">
        <div className="space-y-6">
        <section className="border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Organizations</h2>
            </div>
            <Dialog open={isCreateOrgOpen} onOpenChange={setIsCreateOrgOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Organization
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Organization</DialogTitle>
                  <DialogDescription>
                    Organizations are top-level security groups for managing files and teams.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Organization Name</label>
                    <Input
                      placeholder="Acme Inc"
                      value={newOrgName}
                      onChange={(e) => {
                        const name = e.target.value
                        setNewOrgName(name)
                        if (!newOrgSlug || newOrgSlug === slugify(newOrgName)) {
                          setNewOrgSlug(slugify(name))
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Slug</label>
                    <Input
                      placeholder="acme-inc"
                      value={newOrgSlug}
                      onChange={(e) => setNewOrgSlug(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOrgOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateOrg} disabled={!newOrgName.trim() || !newOrgSlug.trim() || isCreatingOrg}>
                    {isCreatingOrg ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {orgs.length > 0 ? (
            <div className="space-y-3">
              {orgs.map((org) => (
                <div
                  key={org.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div>
                    <p className="font-medium">{org.name}</p>
                    <p className="text-sm text-muted-foreground">@{org.slug} • {org.role || 'member'}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedOrg(selectedOrg?.id === org.id ? null : org)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {selectedOrg?.id === org.id ? 'Hide' : 'Manage'}
                  </Button>
                </div>
              ))}

              {selectedOrg && (
                <div className="mt-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">Members of {selectedOrg.name}</h3>
                    <Dialog open={isAddUserToOrgOpen} onOpenChange={setIsAddUserToOrgOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add User
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add User to {selectedOrg.name}</DialogTitle>
                          <DialogDescription>
                            Select an existing system user to add to this organization.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">User</label>
                            <Select value={newOrgMemberUserId} onValueChange={setNewOrgMemberUserId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a user" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableUsersForOrg.map((u) => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.display_name || u.email} ({u.email})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Role</label>
                            <Select value={newOrgMemberRole} onValueChange={setNewOrgMemberRole}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">member</SelectItem>
                                <SelectItem value="admin">admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsAddUserToOrgOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleAddOrgMember} disabled={!newOrgMemberUserId || isAddingOrgMember}>
                            {isAddingOrgMember ? 'Adding…' : 'Add'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {orgMembers.length > 0 ? (
                    <div className="space-y-2">
                      {orgMembers.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                          <div>
                            <p className="text-sm font-medium">{member.display_name || member.email}</p>
                            <p className="text-xs text-muted-foreground">{member.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select value={member.role} onValueChange={(v: string) => handleUpdateOrgMemberRole(member.user_id, v)}>
                              <SelectTrigger className="w-[120px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">member</SelectItem>
                                <SelectItem value="admin">admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={member.user_id === user?.id}
                              onClick={() => handleRemoveOrgMember(member.user_id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No members found</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              You're not a member of any organizations. Create one to get started.
            </p>
          )}
        </section>
        </div>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams">
        <section className="border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Teams</h2>
            </div>
            <Dialog open={isCreateTeamOpen} onOpenChange={setIsCreateTeamOpen}>
              <DialogTrigger asChild>
                <Button size="sm" disabled={orgs.length === 0}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Team
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Team</DialogTitle>
                  <DialogDescription>
                    Teams belong to an organization and allow groups to share files.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Organization</label>
                    <Select value={newTeamOrgId} onValueChange={setNewTeamOrgId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {orgs.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Team Name</label>
                    <Input
                      placeholder="Engineering"
                      value={newTeamName}
                      onChange={(e) => {
                        const name = e.target.value
                        setNewTeamName(name)
                        if (!newTeamSlug || newTeamSlug === slugify(newTeamName)) {
                          setNewTeamSlug(slugify(name))
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Slug</label>
                    <Input
                      placeholder="engineering"
                      value={newTeamSlug}
                      onChange={(e) => setNewTeamSlug(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateTeamOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateTeam}
                    disabled={!newTeamName.trim() || !newTeamSlug.trim() || !newTeamOrgId || isCreatingTeam}
                  >
                    {isCreatingTeam ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {allTeams.length > 0 ? (
            <div className="space-y-3">
              {allTeams.map(({ team, org }) => (
                <div
                  key={team.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div>
                    <p className="font-medium">{team.name}</p>
                    <p className="text-sm text-muted-foreground">@{team.slug} • {org.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedTeam((prev) => (prev?.team.id === team.id ? null : { team, org }))
                        setSelectedOrg((prev) => (prev?.id === org.id ? prev : org))
                      }}
                    >
                      {selectedTeam?.team.id === team.id ? 'Hide' : 'Manage'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteTeam(team, org)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}

              {selectedTeam && (
                <div className="mt-4 p-4 border rounded-lg">
                  <h3 className="font-medium mb-3">Members of {selectedTeam.team.name}</h3>
                  {isLoadingTeam ? (
                    <p className="text-sm text-muted-foreground">Loading team…</p>
                  ) : (
                    <>
                      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                        <div className="flex-1">
                          <label className="text-sm font-medium">Add member</label>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {isLoadingTeamOrgMembers
                              ? 'Loading org users…'
                              : `Org users: ${teamOrgMembers.length} • Eligible: ${teamOrgMembers.filter((m) => !(teamDetails?.members || []).some((tm) => tm.user_id === m.user_id)).length}`}
                          </div>
                          <Select value={newTeamMemberUserId} onValueChange={setNewTeamMemberUserId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a user" />
                            </SelectTrigger>
                            <SelectContent>
                              {isLoadingTeamOrgMembers ? (
                                <div className="px-2 py-1.5 text-sm text-muted-foreground">Loading users…</div>
                              ) : (
                                (() => {
                                  const eligible = teamOrgMembers.filter(
                                    (m) => !(teamDetails?.members || []).some((tm) => tm.user_id === m.user_id)
                                  )

                                  if (eligible.length === 0) {
                                    return (
                                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                        No eligible users found for this org.
                                      </div>
                                    )
                                  }

                                  return eligible.map((m) => (
                                    <SelectItem key={m.user_id} value={m.user_id}>
                                      {m.display_name || m.email}
                                    </SelectItem>
                                  ))
                                })()
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-full sm:w-48">
                          <label className="text-sm font-medium">Role</label>
                          <Select value={newTeamMemberRole} onValueChange={setNewTeamMemberRole}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="member">member</SelectItem>
                              <SelectItem value="admin">admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          onClick={handleAddTeamMember}
                          disabled={isLoadingTeamOrgMembers || !newTeamMemberUserId || isAddingTeamMember}
                        >
                          {isAddingTeamMember ? 'Adding…' : 'Add'}
                        </Button>
                      </div>

                      {(teamDetails?.members || []).length > 0 ? (
                        <div className="space-y-2">
                          {(teamDetails?.members || []).map((m) => (
                            <div key={m.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                              <div>
                                <p className="text-sm font-medium">{m.display_name || m.email || m.user_id}</p>
                                <p className="text-xs text-muted-foreground">{m.role}</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveTeamMember(m.user_id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No team members found</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : orgs.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              No teams yet. Create a team to collaborate with others.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Create an organization first, then you can create teams within it.
            </p>
          )}
        </section>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags">
        <section className="border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <TagIcon className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Tags</h2>
            </div>
            <Dialog open={isCreateTagOpen} onOpenChange={setIsCreateTagOpen}>
              <DialogTrigger asChild>
                <Button size="sm" disabled={orgs.length === 0}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Tag
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Tag</DialogTitle>
                  <DialogDescription>
                    Tags help organize and categorize files. They are scoped to an organization.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Tag Name</label>
                    <Input
                      placeholder="Important"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        placeholder="#3b82f6"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateTagOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateTag} disabled={!newTagName.trim() || isCreatingTag}>
                    {isCreatingTag ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {orgs.length > 1 && (
            <div className="mb-4">
              <label className="text-sm font-medium">Organization</label>
              <Select value={tagsOrgId} onValueChange={setTagsOrgId}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLoadingTags ? (
            <p className="text-sm text-muted-foreground">Loading tags…</p>
          ) : orgTags.length > 0 ? (
            <div className="space-y-2">
              {orgTags.map((tag) => (
                <div key={tag.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  {editingTag?.id === tag.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="color"
                        value={editTagColor}
                        onChange={(e) => setEditTagColor(e.target.value)}
                        className="w-8 h-8 rounded border cursor-pointer"
                      />
                      <Input
                        value={editTagName}
                        onChange={(e) => setEditTagName(e.target.value)}
                        className="flex-1 max-w-xs"
                      />
                      <Button size="sm" onClick={handleUpdateTag}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingTag(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: tag.color || '#6b7280' }}
                        />
                        <span className="font-medium">{tag.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => startEditTag(tag)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteTag(tag.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : orgs.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              No tags yet. Create tags to help organize your files.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Create an organization first, then you can create tags.
            </p>
          )}
        </section>
        </TabsContent>

        {/* System Settings Tab */}
        <TabsContent value="settings">
        <section className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Cog className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">System Settings</h2>
          </div>

          <div className="rounded-md border p-4 space-y-3">
            <div>
              <p className="font-medium">Allow regular users to create public file links</p>
              <p className="text-sm text-muted-foreground">
                When disabled, only admins can create and manage public share links.
              </p>
            </div>

            {isLoadingPublicShareSetting ? (
              <p className="text-sm text-muted-foreground">Loading setting...</p>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant={allowRegularUsersPublicFileLinks ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAllowRegularUsersPublicFileLinks(true)}
                >
                  Yes
                </Button>
                <Button
                  variant={!allowRegularUsersPublicFileLinks ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAllowRegularUsersPublicFileLinks(false)}
                >
                  No
                </Button>
                <Button onClick={handleSavePublicSharingSetting} disabled={isSavingPublicShareSetting} size="sm">
                  {isSavingPublicShareSetting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>
        </section>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <section className="border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">System Logs</h2>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={logsFilter}
                  onChange={(e) => setLogsFilter(e.target.value as any)}
                  className="px-3 py-1.5 border rounded-md text-sm"
                >
                  <option value="all">All Levels</option>
                  <option value="error">Error</option>
                  <option value="warn">Warning</option>
                  <option value="info">Info</option>
                </select>
                <Button onClick={loadLogs} disabled={isLoadingLogs} size="sm">
                  {isLoadingLogs ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </div>

            {isLoadingLogs ? (
              <p className="text-sm text-muted-foreground">Loading logs...</p>
            ) : logs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No logs available. Click Refresh to load.</p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {logs
                  .filter((log) => logsFilter === 'all' || log.level === logsFilter)
                  .map((log, idx) => (
                    <div
                      key={idx}
                      className={`text-xs font-mono p-2 rounded border ${
                        log.level === 'error'
                          ? 'bg-red-50 border-red-200 text-red-900'
                          : log.level === 'warn'
                            ? 'bg-amber-50 border-amber-200 text-amber-900'
                            : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                            log.level === 'error'
                              ? 'bg-red-200 text-red-800'
                              : log.level === 'warn'
                                ? 'bg-amber-200 text-amber-800'
                                : 'bg-slate-200 text-slate-800'
                          }`}
                        >
                          {log.level.toUpperCase()}
                        </span>
                        {log.source && <span className="text-slate-600">[{log.source}]</span>}
                      </div>
                      <div className="break-all">{log.message}</div>
                    </div>
                  ))}
              </div>
            )}
          </section>
        </TabsContent>

      </Tabs>
      )}
    </div>
  )
}
